import React, { useEffect, useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

// Extra deals for a whole cohort at once — same credits shops buy, so they never expire.
export const AdminGiftCredits = () => {
  const { toast } = useToast();
  const [segments, setSegments] = useState([]);
  const [segment, setSegment] = useState("");
  const [credits, setCredits] = useState("5");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/broadcast/segments")
      .then((r) => setSegments((r.data.segments || []).filter((s) => s.key !== "shoppers")))
      .catch(() => setSegments([]));
  }, []);

  const picked = segments.find((s) => s.key === segment);
  const n = Number(credits) || 0;

  const give = async () => {
    if (!window.confirm(`Give ${n} extra deal(s) to every account in "${picked?.label}"?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gift-credits", { segment, credits: n, note: note.trim() });
      toast({ title: `${data.credits} deals each to ${data.shops} shops`, description: `${data.emailed} emails went out.` });
      setNote(""); setSegment("");
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not give those out" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-gift-credits">
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <Gift size={18} className="text-[#16a34a]" /> Gift extra deals
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">
        Adds deal credits to every shop in the audience. They never expire, are used only after a
        shop's monthly quota runs out, and everyone gets an email and an inbox note.
      </p>

      <div className="space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="gift-segments">
          {segments.map((s) => (
            <button key={s.key} onClick={() => setSegment(s.key)} data-testid={`gift-segment-${s.key}`}
              className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${segment === s.key
                ? "bg-[#0f2a1a] text-white border-[#0f2a1a]"
                : "bg-card text-foreground border-border hover:border-[#16a34a]"}`}>
              {s.label} · {s.count}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground">Deals each</label>
            <input value={credits} onChange={(e) => setCredits(e.target.value.replace(/\D/g, ""))}
              inputMode="numeric" data-testid="gift-credits-input"
              className="mt-1 w-24 bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
          </div>
          <button onClick={give} disabled={busy || !segment || n < 1 || n > 200} data-testid="gift-send-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white px-4 py-2.5 rounded-xl transition-colors">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Gift size={14} />}
            {picked ? `Give ${n || 0} each to ${picked.count} shops` : "Pick an audience"}
          </button>
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="gift-note"
          placeholder="Optional line to explain why — e.g. Sorry about Tuesday's outage, here's five on us."
          className="w-full bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none resize-none" />
      </div>
    </div>
  );
};

export default AdminGiftCredits;
